import { Box, Text } from '@mantine/core'

import { Button, ButtonText, Tooltip } from '@percent/lemonade'
import type {
  CancelButtonProps,
  PrimaryButtonProps,
  SecondaryButtonProps,
  WizardHeaderProps,
} from './wizard-header.props'
import classes from './wizard-header.module.scss'
import { clsx } from '@mantine/styles'

function PrimaryButton({
  onPrimaryBtn,
  primaryBtnLabel,
  isPrimaryBtnDisabled = false,
  isPrimaryBtnLoading = false,
  primaryBtnDisabledTooltipCopy,
  primaryBtnTestId = 'publishBtn',
}: PrimaryButtonProps) {
  if (isPrimaryBtnDisabled && !isPrimaryBtnLoading) {
    return (
      <Tooltip content={primaryBtnDisabledTooltipCopy}>
        <div data-testid="wrapper-disabled-confirm-button">
          <Button
            size="small"
            onClick={onPrimaryBtn}
            disabled={isPrimaryBtnDisabled}
            loading={isPrimaryBtnLoading}
            data-testid={primaryBtnTestId}
          >
            {primaryBtnLabel}
          </Button>
        </div>
      </Tooltip>
    )
  }

  return (
    <Button
      size="small"
      onClick={onPrimaryBtn}
      disabled={isPrimaryBtnDisabled}
      loading={isPrimaryBtnLoading}
      data-testid={primaryBtnTestId}
    >
      {primaryBtnLabel}
    </Button>
  )
}

function SecondaryButton({
  onSecondaryBtn,
  secondaryBtnLabel,
  isSecondaryBtnDisabled = false,
  isSecondaryBtnLoading = false,
  secondaryBtnDisabledTooltipCopy,
  secondaryBtnTestId = 'wizard-header-secondary-btn',
}: SecondaryButtonProps) {
  if (!onSecondaryBtn) {
    return null
  }

  if (isSecondaryBtnDisabled && !isSecondaryBtnLoading) {
    return (
      <Tooltip content={secondaryBtnDisabledTooltipCopy}>
        <div data-testid="wrapper-disabled-secondary-button">
          <Button
            size="small"
            variant="secondary"
            onClick={onSecondaryBtn}
            disabled={isSecondaryBtnDisabled}
            loading={isSecondaryBtnLoading}
            data-testid={secondaryBtnTestId}
          >
            {secondaryBtnLabel}
          </Button>
        </div>
      </Tooltip>
    )
  }

  return (
    <Button
      size="small"
      variant="secondary"
      onClick={onSecondaryBtn}
      disabled={isSecondaryBtnDisabled}
      loading={isSecondaryBtnLoading}
      data-testid={secondaryBtnTestId}
    >
      {secondaryBtnLabel}
    </Button>
  )
}

function CancelButton({
  onCancel,
  cancelBtnLabel,
  cancelBtnTestId = 'cancelBtn',
}: Readonly<CancelButtonProps>) {
  if (!onCancel) {
    return null
  }

  return (
    <ButtonText onClick={onCancel} data-testid={cancelBtnTestId}>
      {cancelBtnLabel}
    </ButtonText>
  )
}

export function WizardHeader({
  title,
  className,
  ...props
}: Readonly<WizardHeaderProps>) {
  return (
    <Box className={clsx(classes.Wrapper, className)}>
      <Text className={classes.Title}>{title}</Text>
      <Box className={classes.ButtonsWrapper}>
        <CancelButton {...props} />
        <SecondaryButton {...props} />
        <PrimaryButton {...props} />
      </Box>
    </Box>
  )
}
